/**
 * Lazy ZIP reader over a Blob/File. Only the central directory is read up front;
 * entries are sliced + inflated on demand, so a 2 GB world zip never has to sit in memory.
 * Works in browsers, web workers, and Node >= 20 (Blob + DecompressionStream).
 */
import { inflate } from "./nbt.ts";

export interface ZipEntry {
  name: string;
  method: number; // 0 = stored, 8 = deflate
  compressedSize: number;
  size: number;
  localHeaderOffset: number;
}

export class ZipReader {
  readonly entries = new Map<string, ZipEntry>();
  readonly blob: Blob;
  private constructor(blob: Blob) {
    this.blob = blob;
  }

  static async open(blob: Blob): Promise<ZipReader> {
    const z = new ZipReader(blob);
    await z.readDirectory();
    return z;
  }

  private async bytes(start: number, end: number) {
    return new Uint8Array(await this.blob.slice(start, end).arrayBuffer());
  }

  private async readDirectory() {
    const size = this.blob.size;
    const tailLen = Math.min(size, 65557 + 20);
    const tail = await this.bytes(size - tailLen, size);
    const dv = new DataView(tail.buffer);
    let eocd = -1;
    for (let i = tail.length - 22; i >= 0; i--) {
      if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd < 0) throw new Error("Not a ZIP file (no end-of-central-directory record)");
    let count = dv.getUint16(eocd + 10, true);
    let cdSize = dv.getUint32(eocd + 12, true);
    let cdOffset = dv.getUint32(eocd + 16, true);
    // ZIP64
    if (cdOffset === 0xffffffff || count === 0xffff) {
      const loc = eocd - 20;
      if (loc >= 0 && dv.getUint32(loc, true) === 0x07064b50) {
        const z64Off = Number(dv.getBigUint64(loc + 8, true));
        const z = await this.bytes(z64Off, z64Off + 56);
        const zv = new DataView(z.buffer);
        count = Number(zv.getBigUint64(32, true));
        cdSize = Number(zv.getBigUint64(40, true));
        cdOffset = Number(zv.getBigUint64(48, true));
      }
    }
    const cd = await this.bytes(cdOffset, cdOffset + cdSize);
    const cv = new DataView(cd.buffer);
    const td = new TextDecoder();
    let p = 0;
    for (let i = 0; i < count && p + 46 <= cd.length; i++) {
      if (cv.getUint32(p, true) !== 0x02014b50) break;
      const method = cv.getUint16(p + 10, true);
      let compressedSize = cv.getUint32(p + 20, true);
      let usize = cv.getUint32(p + 24, true);
      const nameLen = cv.getUint16(p + 28, true);
      const extraLen = cv.getUint16(p + 30, true);
      const commentLen = cv.getUint16(p + 32, true);
      let localHeaderOffset = cv.getUint32(p + 42, true);
      const name = td.decode(cd.subarray(p + 46, p + 46 + nameLen)).replace(/\\/g, "/");
      // ZIP64 extra field
      let e = p + 46 + nameLen;
      const eEnd = e + extraLen;
      while (e + 4 <= eEnd) {
        const id = cv.getUint16(e, true);
        const len = cv.getUint16(e + 2, true);
        if (id === 0x0001) {
          let q = e + 4;
          if (usize === 0xffffffff) { usize = Number(cv.getBigUint64(q, true)); q += 8; }
          if (compressedSize === 0xffffffff) { compressedSize = Number(cv.getBigUint64(q, true)); q += 8; }
          if (localHeaderOffset === 0xffffffff) { localHeaderOffset = Number(cv.getBigUint64(q, true)); q += 8; }
        }
        e += 4 + len;
      }
      if (!name.endsWith("/")) this.entries.set(name, { name, method, compressedSize, size: usize, localHeaderOffset });
      p += 46 + nameLen + extraLen + commentLen;
    }
  }

  has(name: string) {
    return this.entries.has(name);
  }

  async read(name: string): Promise<Uint8Array> {
    const e = this.entries.get(name);
    if (!e) throw new Error(`ZIP entry not found: ${name}`);
    const lh = await this.bytes(e.localHeaderOffset, e.localHeaderOffset + 30);
    const lv = new DataView(lh.buffer);
    if (lv.getUint32(0, true) !== 0x04034b50) throw new Error(`Bad local header for ${name}`);
    const start = e.localHeaderOffset + 30 + lv.getUint16(26, true) + lv.getUint16(28, true);
    const raw = await this.bytes(start, start + e.compressedSize);
    if (e.method === 0) return raw;
    if (e.method === 8) return inflate(raw, "deflate-raw");
    throw new Error(`Unsupported ZIP compression method ${e.method} (${name})`);
  }
}
